import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  PencilIcon,
  TrashIcon,
  NoSymbolIcon,
  PlusIcon,
  ChevronDownIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import PredefinedModal from './PredefinedModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const PredefinedList = () => {
  const [messages, setMessages] = useState([]);
  const [clients, setClients] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState({});
  const [filterPhone, setFilterPhone] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    fetchMessages();
    fetchClients();
  }, []);

  useEffect(() => {
    groupMessages();
  }, [messages, clients, filterPhone]);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API_URL}/predefined`);
      setMessages(res.data);
    } catch (err) {
      console.error('Erro ao buscar mensagens:', err);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API_URL}/clients`);
      setClients(res.data);
    } catch (err) {
      console.error('Erro ao buscar clientes:', err);
    }
  };

  const getClientName = (phone) => {
    const client = clients.find((c) => c.phone === phone);
    return client ? client.name : 'Unknown';
  };

  const groupMessages = () => {
    const grouped = {};
    messages.forEach((msg) => {
      const phone = msg.clientPhone;
      if (!grouped[phone]) {
        grouped[phone] = [];
      }
      grouped[phone].push(msg);
    });

    // Filter
    const filtered = {};
    Object.entries(grouped).forEach(([phone, msgs]) => {
      if (
        filterPhone.trim() === '' ||
        phone.toLowerCase().includes(filterPhone.trim().toLowerCase())
      ) {
        filtered[phone] = msgs;
      }
    });

    setFilteredGroups(filtered);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja deletar esta mensagem?')) {
      try {
        await axios.delete(`${API_URL}/predefined/${id}`);
        fetchMessages();
      } catch (err) {
        alert(err.response?.data?.error || 'Erro ao deletar.');
      }
    }
  };

  const handleInactivate = async (id) => {
    if (window.confirm('Deseja inativar esta mensagem?')) {
      try {
        await axios.put(`${API_URL}/predefined/${id}/inactivate`);
        fetchMessages();
      } catch (err) {
        alert(err.response?.data?.error || 'Erro ao inativar.');
      }
    }
  };

  const handleEdit = (msg) => {
    setEditing(msg);
    setShowModal(true);
  };

  // --- Informational Banner (standard) ---
  const InfoBanner = () => (
    <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm">
      <div className="flex items-start gap-3">
        <InformationCircleIcon className="h-5 w-5 flex-shrink-0" />
        <div>
          <h3 className="text-base font-semibold">Manage Predefined Messages</h3>
          <p className="mt-1 text-gray-700">
            Create, edit, and organize predefined responses linked to client phone numbers.
            These messages can be used by the AI or sent manually to speed up communication.
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="px-6 py-4">
      {/* Banner padrão */}
      <InfoBanner />

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <span role="img" aria-label="icon">📑</span> Predefined Messages
        </h2>
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded flex items-center gap-1 hover:bg-blue-700"
          onClick={() => {
            setEditing(null);
            setShowModal(true);
          }}
        >
          <PlusIcon className="h-4 w-4" /> Add Message
        </button>
      </div>

      <input
        type="text"
        placeholder="Filter by phone..."
        className="form-control mb-4 w-full border border-gray-300 rounded px-3 py-2"
        value={filterPhone}
        onChange={(e) => setFilterPhone(e.target.value)}
      />

      <div className="space-y-4">
        {Object.entries(filteredGroups).map(([phone, msgs]) => (
          <details
            key={phone}
            className="border rounded-md shadow bg-white"
            open={true}
          >
            <summary className="cursor-pointer select-none px-4 py-3 bg-gray-100 flex justify-between items-center text-sm font-semibold">
              <div className="flex flex-col">
                <span className="text-gray-800">
                  👤 {getClientName(phone)} - {phone}
                </span>
                <span className="text-xs text-gray-500">{msgs.length} message(s)</span>
              </div>
              <ChevronDownIcon className="w-4 h-4 text-gray-500" />
            </summary>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white text-sm">
                <thead>
                  <tr className="text-gray-700 border-b">
                    <th className="px-4 py-2 text-left">Message</th>
                    <th className="px-4 py-2 text-left">Use AI</th>
                    <th className="px-4 py-2 text-left">Used</th>
                    <th className="px-4 py-2 text-left">Status</th>
                    <th className="px-4 py-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {msgs.map((msg) => (
                    <tr key={msg._id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-2">
                        {msg.message.length > 50 ? msg.message.slice(0, 50) + '...' : msg.message}
                      </td>
                      <td className="px-4 py-2">{msg.useAI ? 'Yes' : 'No'}</td>
                      <td className="px-4 py-2">{msg.used ? 'Yes' : 'No'}</td>
                      <td className="px-4 py-2">
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${
                            msg.active ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {msg.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-2 flex gap-2 items-center">
                        {msg.used === false ? (
                          <>
                            <button onClick={() => handleEdit(msg)} title="Edit">
                              <PencilIcon className="h-4 w-4 text-blue-600 hover:text-blue-800" />
                            </button>
                            <button onClick={() => handleDelete(msg._id)} title="Delete">
                              <TrashIcon className="h-4 w-4 text-red-600 hover:text-red-800" />
                            </button>
                          </>
                        ) : msg.active ? (
                          <button onClick={() => handleInactivate(msg._id)} title="Inactivate">
                            <NoSymbolIcon className="h-4 w-4 text-yellow-600 hover:text-yellow-800" />
                          </button>
                        ) : (
                          <span className="text-gray-400 italic">Inactive</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        ))}
      </div>

      <PredefinedModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSaved={() => {
          setShowModal(false);
          fetchMessages();
        }}
        mode={editing ? 'edit' : 'create'}
        message={editing}
      />
    </div>
  );
};

export default PredefinedList;
